import { EmptyResponseEntity } from "types/emptyResponseEntity";
import { BTLoginTypes } from "types/enum";

import { APIHandler } from "utilities/apiHandler";

import {
    LinkedAccountingEntityResponse,
    UpdateAccountingResponse,
} from "commonComponents/entity/accounting/LinkedAccountingEntity/LinkedAccountingEntity.api.types";
import { JobAccessEntity } from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";
import { type CertificateType } from "commonComponents/entity/sub/CertificateFile/CertificateFile.types";
import { type IDocumentContextMenuHandler } from "commonComponents/utilities/DocumentContextMenu/DocumentContextMenu.api.handler";
import {
    DocumentContextMenuEntity,
    type IDocumentContextMenuRequest,
} from "commonComponents/utilities/DocumentContextMenu/DocumentContextMenu.types";

import { type IFilterFormValues } from "entity/filters/Filter/Filter.api.types";
import { getFilterString } from "entity/filters/filters.utils";

import {
    AgreementReleaseResponse,
    type IAgreementReleaseRequest,
    type ICertificateRequest,
    type IReplaceAgreementRequest,
    ReplaceAgreementResponse,
    type SubSaveFormRequest,
    SubVendorCreateUpdateResponse,
    SubVendorEntity,
    SubVendorToggleEnableResponse,
} from "./SubVendor.api.types";

export interface ISubVendorHandler {
    get(id: number): Promise<SubVendorEntity>;
    getDefault(): Promise<SubVendorEntity>;
    create(newValues: SubSaveFormRequest): Promise<SubVendorCreateUpdateResponse>;
    update(
        id: number,
        formData: SubSaveFormRequest,
        confirmUpdate: boolean,
    ): Promise<SubVendorCreateUpdateResponse>;
    delete(id: number): Promise<EmptyResponseEntity>;
    setSubEnabledStatus(subId: number, enabled: boolean): Promise<SubVendorToggleEnableResponse>;
    requestCert(subId: number, data: ICertificateRequest): Promise<EmptyResponseEntity>;
    approveCert(subId: number, data: ICertificateRequest): Promise<EmptyResponseEntity>;
    getJobAccess(id: number, filter: IFilterFormValues | undefined): Promise<JobAccessEntity>;
    sendMassInviteInvitationRequest(
        subId: number,
        inviteMessage: string | null,
    ): Promise<EmptyResponseEntity>;
    getAccountingInfo(id: number): Promise<LinkedAccountingEntityResponse>;
    updateAccountingVendor(id: number): Promise<UpdateAccountingResponse>;
    unlinkAccountingVendor(id: number): Promise<EmptyResponseEntity>;
    releaseTradeAgreement(request: IAgreementReleaseRequest): Promise<AgreementReleaseResponse>;
    replaceTradeAgreement(request: IReplaceAgreementRequest): Promise<ReplaceAgreementResponse>;
}

export class SubVendorHandler implements ISubVendorHandler {
    async get(id: number): Promise<SubVendorEntity> {
        return await APIHandler(`/api/Subs/${id}`, {
            method: "GET",
            responseType: SubVendorEntity,
        });
    }

    async getDefault(): Promise<SubVendorEntity> {
        return await APIHandler(`/api/Subs/DefaultInfo`, {
            method: "GET",
            responseType: SubVendorEntity,
        });
    }

    async create(request: SubSaveFormRequest): Promise<SubVendorCreateUpdateResponse> {
        return await APIHandler(`/api/Subs`, {
            method: "POST",
            data: request,
            responseType: SubVendorCreateUpdateResponse,
        });
    }

    async update(
        id: number,
        request: SubSaveFormRequest,
        confirmUpdate: boolean,
    ): Promise<SubVendorCreateUpdateResponse> {
        const headers: Record<string, string> = {};
        if (confirmUpdate) {
            headers["Accept-confirm"] = "confirm-job-remove";
        } else {
            headers["Accept-confirm"] = "";
        }
        return await APIHandler(`/api/Subs/${id}`, {
            method: "PUT",
            data: request,
            responseType: SubVendorCreateUpdateResponse,
            headers: headers,
        });
    }

    async delete(id: number): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Subs/${id}`, {
            method: "DELETE",
            responseType: EmptyResponseEntity,
        });
    }

    async setSubEnabledStatus(
        subId: number,
        enabled: boolean,
    ): Promise<SubVendorToggleEnableResponse> {
        return await APIHandler(`/api/Subs/SetSubEnabledStatus`, {
            method: "PUT",
            data: { subId, enabled },
            responseType: SubVendorToggleEnableResponse,
        });
    }

    async requestCert(subId: number, data: ICertificateRequest): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Subs/${subId}/RequestCertificateApproval`, {
            method: "POST",
            data: data,
            responseType: EmptyResponseEntity,
        });
    }
    async approveCert(subId: number, data: ICertificateRequest): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Subs/${subId}/ApproveCertificate`, {
            method: "PUT",
            data: data,
            responseType: EmptyResponseEntity,
        });
    }
    async getJobAccess(
        id: number,
        filter: IFilterFormValues | undefined,
    ): Promise<JobAccessEntity> {
        const filterString = getFilterString(filter);
        return await APIHandler(`/api/Subs/JobAccess?id=${id}&filter=${filterString}`, {
            method: "GET",
            responseType: JobAccessEntity,
        });
    }
    async sendMassInviteInvitationRequest(subId: number, inviteMessage: string | null) {
        return await APIHandler(`/api/jobsites/SendMassSubInvitationRequest`, {
            method: "PUT",
            data: { subsToInvite: [subId], subInviteMessage: inviteMessage },
            responseType: EmptyResponseEntity,
        });
    }
    async getAccountingInfo(id: number): Promise<LinkedAccountingEntityResponse> {
        return await APIHandler(
            `/api/Accounting/${id}/LinkedEntityInfo?loginType=${BTLoginTypes.SUBS}`,
            { method: "GET", responseType: LinkedAccountingEntityResponse },
        );
    }
    async updateAccountingVendor(id: number): Promise<UpdateAccountingResponse> {
        return await APIHandler(`/api/Subs/${id}/SyncAccountingVendor`, {
            method: "PUT",
            responseType: UpdateAccountingResponse,
        });
    }
    async unlinkAccountingVendor(id: number): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Accounting/${id}/UpdateVendorLink`, {
            method: "PUT",
            data: { stringId: -1 },
            responseType: EmptyResponseEntity,
        });
    }
    async releaseTradeAgreement(
        request: IAgreementReleaseRequest,
    ): Promise<AgreementReleaseResponse> {
        return await APIHandler(`/api/TradeAgreement/ReleaseAgreement`, {
            method: "PUT",
            data: request,
            responseType: AgreementReleaseResponse,
        });
    }
    async replaceTradeAgreement(
        request: IReplaceAgreementRequest,
    ): Promise<ReplaceAgreementResponse> {
        return await APIHandler(`/api/TradeAgreement/ReplaceAgreement`, {
            method: "PUT",
            data: request,
            responseType: ReplaceAgreementResponse,
        });
    }
}

interface ISubVendorCertificateContextMenuRequest {
    id: number;
    certType: CertificateType;
}

export class SubVendorCertificateContextMenuHandler implements IDocumentContextMenuHandler {
    async GetDocumentContextMenu(
        data: IDocumentContextMenuRequest,
    ): Promise<DocumentContextMenuEntity> {
        const request: ISubVendorCertificateContextMenuRequest = {
            id: data.associatedEntityId,
            certType: data.docType as CertificateType,
        };
        return await APIHandler(`/api/Subs/CertificateContextMenu`, {
            method: "GET",
            data: request,
            responseType: DocumentContextMenuEntity,
        });
    }
}
